import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Calculator, RefreshCw } from "lucide-react";

const fmtL = (v) => (v == null ? "—" : `₹${v}L`);
const METHOD_COLORS = { scorecard: "#FF8800", berkus: "#00B0FF", vc_method: "#00E676" };

export function ValuationEngine({ startup, onChanged, testid = "valuation-engine" }) {
  const [running, setRunning] = useState(false);
  const v = startup.valuation_engine;
  const canRun = (startup.member_role || "owner") !== "viewer";

  const run = async () => {
    setRunning(true);
    try {
      await api.post(`/portal/startups/${startup.id}/valuation`);
      toast.success("Valuation engine updated");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="glass rounded-xl p-5 sm:p-6" data-testid={testid}>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <p className="font-mono-tech text-[10px] tracking-[0.3em] text-[#00E676] uppercase flex items-center gap-2">
          <Calculator className="w-3.5 h-3.5" /> Valuation Engine — 3 methods
        </p>
        {canRun && (
          <Button variant="outline" size="sm" onClick={run} disabled={running} data-testid={`${testid}-run`} className="border-white/15 text-slate-300 hover:bg-white/5">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${running ? "animate-spin" : ""}`} /> {v ? "Recalculate" : "Run Valuation Engine"}
          </Button>
        )}
      </div>

      {!v ? (
        <p className="text-sm text-slate-400" data-testid={`${testid}-empty`}>
          Compute an indicative pre-money valuation using the Scorecard, Berkus and VC methods — built from your questionnaire answers and AI score.
        </p>
      ) : (
        <div className="space-y-5" data-testid={`${testid}-results`}>
          <div className="grid sm:grid-cols-3 gap-4">
            {Object.entries(v.methods || {}).map(([key, m]) => (
              <div key={key} className="rounded-lg border border-white/10 p-4" data-testid={`${testid}-method-${key}`}>
                <p className="font-mono-tech text-[10px] tracking-widest uppercase" style={{ color: METHOD_COLORS[key] || "#FF8800" }}>{m.label}</p>
                <p className="font-display text-xl font-extrabold text-white mt-1.5">{m.value_lakhs > 0 ? fmtL(m.value_lakhs) : "n/a"}</p>
                <p className="text-[11px] text-slate-500 mt-1.5 leading-relaxed">{m.note}</p>
              </div>
            ))}
          </div>

          {v.methods?.berkus?.elements && (
            <div className="rounded-lg border border-white/10 p-4">
              <p className="font-mono-tech text-[10px] tracking-widest text-[#00B0FF] uppercase mb-2.5">Berkus element credits (₹L)</p>
              <div className="flex items-end gap-3 h-24">
                {Object.entries(v.methods.berkus.elements).map(([label, val]) => (
                  <div key={label} className="flex flex-col items-center gap-1 flex-1 min-w-0">
                    <span className="text-[10px] font-mono-tech text-slate-400">{val}</span>
                    <div className="w-full rounded-t bg-gradient-to-t from-[#00B0FF]/50 to-[#00B0FF]/90" style={{ height: `${Math.max(4, (val / 50) * 100)}%` }} />
                    <span className="text-[8px] font-mono-tech text-slate-600 text-center leading-tight">{label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between flex-wrap gap-2 rounded-lg border border-[#00E676]/25 bg-[#00E676]/[0.04] px-4 py-3" data-testid={`${testid}-range`}>
            <p className="text-xs text-slate-300">Indicative pre-money range</p>
            <p className="font-display font-bold text-white">{fmtL(v.range?.low_lakhs)} – {fmtL(v.range?.high_lakhs)} <span className="text-slate-500 font-normal text-xs">(mid {fmtL(v.range?.mid_lakhs)})</span></p>
          </div>

          <p className="text-[10px] text-slate-500 leading-relaxed">{v.disclaimer}</p>
          <p className="text-[10px] text-slate-600 font-mono-tech">Computed {new Date(v.computed_at).toLocaleString("en-IN")} · stage: {v.stage} · AI score used: {v.ai_score_used}</p>
        </div>
      )}
    </div>
  );
}
